import { usePlaybackCurrentTs } from '@app/core/playback/hooks/usePlayback';
import { LiveTopic } from '../../live-topics-types';
import { toTimestamp } from '@mi-front/core';
import { StreamEl } from '@mi-front/ui';
import { STREAM_UI_CONFIG } from '../../../../consts';
import { getTopicDisplayTitle } from '../../helpers/get-topic-display-title';
import { getCategoryColor } from '../../shared/topic-category-chip/category-colors';
import { StickyPillLabel, TopicPill } from './elements';

const TRACK_HEIGHT = STREAM_UI_CONFIG.focusTracksHeight;
const CONTENT_INSET_Y = STREAM_UI_CONFIG.trackContentInsetY;
const PILL_HEIGHT = TRACK_HEIGHT - 2 * CONTENT_INSET_Y;
const PILL_OFFSET_Y = CONTENT_INSET_Y;

/**
 * Renders a single topic segment on the Live Topics track: pill with display title (refined or canonical).
 * When onMouseEnter/onMouseLeave are provided, the parent handles hover (e.g. for a custom popup).
 * When omitted, the parent typically wraps this in HoverPopover so the popover handles hover.
 * The pill is focused when the playback playhead is within the segment interval, or when isFocused is passed.
 * `zIndex` should increase with chronological order so later pills stack above earlier ones when they overlap.
 */
export function TopicSegmentPill({
  topic,
  onMouseEnter,
  onMouseLeave,
  onClick,
  isFocused: isFocusedProp,
  zIndex = 1,
}: {
  topic: LiveTopic;
  onMouseEnter?: (e: React.MouseEvent) => void;
  onMouseLeave?: () => void;
  /** When set, clicking the pill triggers this (e.g. play at segment start). */
  onClick?: () => void;
  isFocused?: boolean;
  /** Stacking order; later topics should pass a higher value so their pills sit above previous ones. */
  zIndex?: number;
}) {
  const currentTs = usePlaybackCurrentTs();
  const startTs = toTimestamp(topic.start_time);
  const endTs = toTimestamp(topic.end_time);
  const isPlayheadInSegment = currentTs !== null && currentTs >= startTs && currentTs < endTs;
  const isFocused = isFocusedProp === true || isPlayheadInSegment;
  const pillColor = getCategoryColor(topic.category);

  return (
    <StreamEl
      ts={startTs}
      position="top"
      offsetY={PILL_OFFSET_Y}
      zIndex={zIndex}
      style={{ pointerEvents: 'auto' }}
    >
      {(scale) => {
        const width = (scale(endTs) ?? 0) - (scale(startTs) ?? 0);

        return (
          <div
            style={{
              width: Math.max(width, 4),
              height: PILL_HEIGHT,
              cursor: onClick ? 'pointer' : undefined,
            }}
            onMouseEnter={onMouseEnter ? (e) => onMouseEnter(e) : undefined}
            onMouseLeave={onMouseLeave ?? undefined}
            onClick={
              onClick
                ? (e) => {
                    e.stopPropagation();
                    onClick();
                  }
                : undefined
            }
          >
            <TopicPill
              color={pillColor}
              style={{
                width: width >= 60 ? width : 'auto',
                maxWidth: width >= 60 ? width : 60,
                height: PILL_HEIGHT,
                maxHeight: PILL_HEIGHT,
              }}
              title={getTopicDisplayTitle(topic)}
              isFocused={isFocused}
            >
              <StickyPillLabel color={pillColor}>{getTopicDisplayTitle(topic)}</StickyPillLabel>
            </TopicPill>
          </div>
        );
      }}
    </StreamEl>
  );
}
